import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Typography,
  Space,
  Divider,
  Tooltip,
  Popover,
  Spin,
  Tabs,
  Empty,
} from 'antd';
import {
  LikeOutlined,
  RollbackOutlined,
  FormOutlined,
  DeleteOutlined,
  SmileOutlined,
  LikeFilled,
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import httpInjectorService from 'services/http-injector.service';
import UserAvatar from '../../../../components/userAvatar/index';
import { toast } from 'react-toastify';
import '../pages/BacklogPage.css';
import { getColorForUsername } from 'components/utils/AvatarColor';
import Cookies from 'js-cookie';
import HistoryWorklog from '../Board/HistoryWorklog';

const { Title, Text } = Typography;

const TaskCommentSection = ({ taskId, projectId, assigneeOptions ,issueTypeId, taskdata, refreshKey}) => {
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [isEditingMain, setIsEditingMain] = useState(false);
  const [comments, setComments] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editText, setEditText] = useState('');
  const [showEmojiFor, setShowEmojiFor] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [activeReply, setActiveReply] = useState(null);
  const [showTagList, setShowTagList] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [cursorPos, setCursorPos] = useState(null);
  const [activeTab, setActiveTab] = useState('comments');
  const [rootParent, setRootParent] = useState(null);
  const [originalText, setOriginalText] = useState('');

  const roleId = Number(Cookies.get('task-role-id'));

  const editorRef = useRef(null);

  const suggestions = [
    '🎉 Looks good!',
    '👋 Need help?',
    '⛔ This is blocked...',
    '🔍 Can you clarify...?',
  ];

  const getInitials = (username) =>
    username
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase();

  const handleOpenEdit = (comment) => {
    setEditText(comment.comment_text); // current text in editor
    setOriginalText(comment.comment_text); // store original before editing
    setEditingId(comment.comment_id);
  };

  const gettaskcomments = async () => {
    setLoading(true);
    try {
      const reqbody = {
        project_id: projectId,
        issue_id: taskId,
      };
      const res = await httpInjectorService.gettaskcomments(reqbody);
      if (res.status === 'success') {
        const formatted = res.data.map(mapCommentIds);
        setComments(formatted);
      } else {
        console.error('Failed to fetch comments:', res.message);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const hexToRgb = (hex) => {
    if (!hex) return '';
    hex = hex.replace('#', '');

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgb(${r}, ${g}, ${b})`;
  };

  const postComment = async (text, parentId = null) => {
    if (!text.trim()) return;
    const username = Cookies.get('username');

    // const taggedUserIds = [
    //   ...new Set(
    //     taggedUsers
    //       .filter((user) => text.includes(`@${user.username}`))
    //       .map((user) => user.user_id),
    //   ),
    // ];

    const assignerHex = getColorForUsername(username);
    const assignerRgb = hexToRgb(assignerHex);

    const taggedUsersArray = taggedUsers
      .filter((user) => text.includes(`@${user.username}`))
      .map((user) => {
        const userHex = getColorForUsername(user.username);
        const userRgb = hexToRgb(userHex);

        return {
          user_id: user.user_id,
          avatar_code: getInitials(user.username),
          avatar_color_code: userRgb,
        };
      });

    const requestBody = {
      project_id: projectId,
      issue_id: taskId,
      issue_type_id:issueTypeId,
      comment_text: text,
      tagged_user_ids: taggedUsersArray,
      work_item_url:`${window.location.origin}/admin/taskBoard/${projectId}/taskDetails/${taskId}`,
      assigner_avatar: [
        {
          assigner_avatar_code: getInitials(username),
          assigner_avatar_color_code: assignerRgb,
        },
      ],
    };

    if (parentId) {
      requestBody.parent_comment_id = parentId;
    }

    try {
      const response = await httpInjectorService.addComments(requestBody);

      if (response.status === 'success') {
        if (parentId) {
          setReplyText('');
          setReplyingTo(null);
        } else {
          setComment('');
          setIsEditingMain(false);
        }

        setTaggedUsers([]);
        gettaskcomments();
      } else {
        console.error('Failed to post comment:', response.message || response);
      }
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      const reqBody = {
        project_id: projectId,
        comment_id: commentId,
      };

      const res = await httpInjectorService.deletetaskcomment(reqBody);

      if (res.status === 'success') {
        // toast.success(res.message, {
        //   position: 'top-right',
        //   autoClose: 3000,
        // });

        gettaskcomments();
      } else {
        // toast.error(res.message, {
        //   position: 'top-right',
        //   autoClose: 3000,
        // });
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Something went wrong while deleting comment');
    }
  };

  const buildMentionRegex = () => {
    const names = assigneeOptions.map((u) => u.username.trim().toLowerCase());
    const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return new RegExp(`@(${escaped.join('|')})`, 'gi');
  };

  const htmlToText = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, '').trim();
  };

  const extractMentions = (htmlText) => {
    const plain = htmlToText(htmlText).toLowerCase();
    const regex = buildMentionRegex();

    const matches = [];
    let match;

    while ((match = regex.exec(plain)) !== null) {
      matches.push(match[1]); // username
    }

    return matches;
  };

  const handleEditComment = async (commentId, updatedText) => {
    const oldMentions = extractMentions(originalText);
    const newMentions = extractMentions(updatedText);

    const addedMentions = newMentions.filter(
      (name) => !oldMentions.includes(name),
    );

    const username = Cookies.get('username');

    const assignerHex = getColorForUsername(username);
    const assignerRgb = hexToRgb(assignerHex);

    // 3️⃣ Build tagged user array ONLY for added users
    const taggedUsersArray = taggedUsers
      .filter((user) => addedMentions.includes(user.username))
      .map((user) => ({
        user_id: user.user_id,
        avatar_code: getInitials(user.username),
        avatar_color_code: hexToRgb(getColorForUsername(user.username)),
      }));

    try {
      const reqBody = {
        project_id: projectId,
        comment_id: commentId,
        comment_text: updatedText,
        previous_text: originalText,
        tagged_user_ids: taggedUsersArray,
        work_item_url:`${window.location.origin}/admin/taskBoard/${projectId}/taskDetails/${taskId}`,
        assigner_avatar: [
          {
            assigner_avatar_code: getInitials(username),
            assigner_avatar_color_code: assignerRgb,
          },
        ],
      };

      const res = await httpInjectorService.edit_comment(reqBody);
      if (res.status === 'success') {
        // toast.success(res.message, {
        //   position: 'top-right',
        //   autoClose: 3000,
        // });
        gettaskcomments();
      } else {
        // toast.error(res.message, {
        //   position: 'top-right',
        //   autoClose: 3000,
        // });
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Something went wrong while updating comment');
    }
  };

  const mapCommentIds = (c) => ({
    id: c.comment_id,
    username: c.username,
    created_at: c.created_at,
    comment_text: c.comment_text,
    liked: false,
    emoji: null,
    replies: Array.isArray(c.replies) ? c.replies.map(mapCommentIds) : [],
  });

  useEffect(() => {
    gettaskcomments();
  }, []);

  const formatDate = (date) =>
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));

  const updateNestedComments = (list, id, updater) =>
    list.map((c) => {
      if (c.id === id) return updater(c);
      if (Array.isArray(c.replies) && c.replies.length > 0) {
        return { ...c, replies: updateNestedComments(c.replies, id, updater) };
      }
      return c;
    });

  const handleLike = (id) => {
    setComments((prev) =>
      updateNestedComments(prev, id, (c) => ({ ...c, liked: !c.liked })),
    );
  };

  const handlePost = () => {
    postComment(comment);
  };

  const handleReplyPost = (rootParentId) => {
    postComment(replyText, rootParentId);
  };

  const handleEditPost = async (commentId) => {
    await handleEditComment(commentId, editText);
    setEditingId(null);
  };

  const handleTagInsert = (user) => {
    const quill = editorRef.current?.getEditor();
    if (!quill) return;

    const tag = `@${user.username} `;

    quill.deleteText(cursorPos - (tagSearch.length + 1), tagSearch.length + 1);

    quill.insertText(cursorPos - (tagSearch.length + 1), tag);

    setTaggedUsers((prev) => {
      if (prev.some((u) => u.user_id === user.user_id)) return prev;
      return [...prev, user];
    });

    setShowTagList(false);
    setTagSearch('');
  };

  const handleEmojiSelect = (emojiObj, id) => {
    setComments((prev) =>
      updateNestedComments(prev, id, (c) => ({ ...c, emoji: emojiObj.emoji })),
    );
    setShowEmojiFor(null);
  };

  const onApplySuggestion = (text) => {
    setIsEditingMain(true);
    setComment(text);
  };

  const renderCommentBox = (c, level = 0, rootParentId = c.id) => (
    <div
      key={c.id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        borderBottom: level === 0 ? '1px solid #f0f0f0' : 'none',
        paddingBottom: 12,
        marginBottom: 12,
        marginTop: 10,
        marginLeft: level > 0 ? 40 : 0,
        position: 'relative',
      }}
    >
      {level > 0 && (
        <div
          style={{
            position: 'absolute',
            left: -20,
            top: 10,
            bottom: 0,
            width: 2,
            background: '#e8e8e8',
          }}
        />
      )}

      <UserAvatar username={c.username} size={level > 0 ? 26 : 30} />
      <div style={{ flex: 1 }}>
        <Text strong>{c.username}</Text>
        <div style={{ fontSize: 12, color: 'gray' }}>
          {formatDate(c.created_at)}
        </div>

        {editingId === c.id ? (
          <div style={{ marginTop: 6 }}>
            <ReactQuill
              ref={editorRef}
              theme="snow"
              value={editText}
              onChange={(value, delta, source, editor) => {
                setEditText(value);

                const pos = editor.getSelection()?.index;
                setCursorPos(pos);

                const textUpToCursor = editor.getText(0, pos);
                const match = textUpToCursor.match(/@([a-zA-Z0-9_]*)$/);

                if (match) {
                  setTagSearch(match[1]);
                  setShowTagList(true);
                } else {
                  setShowTagList(false);
                }
              }}
              placeholder="Write a comment..."
            />
            {renderTagList()}

            <Space>
              <Button type="primary" onClick={() => handleEditPost(c.id)}>
                Save
              </Button>
              <Button onClick={() => setEditingId(null)}>Cancel</Button>
            </Space>
          </div>
        ) : (
          <div
            style={{ marginTop: 6, marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: c.comment_text }}
          />
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 16, fontSize: 14 }}>
          <Tooltip title="Reply">
            <RollbackOutlined
              style={{ cursor: 'pointer', color: '#595959' }}
              onClick={() => {
                setReplyingTo(c.id);
                setRootParent(rootParentId);
              }}
            />
          </Tooltip>

          {/* <Tooltip title="Like">
            {c.liked ? (
              <LikeFilled
                style={{ cursor: 'pointer', color: '#1677ff' }}
                onClick={() => handleLike(c.id)}
              />
            ) : (
              <LikeOutlined
                style={{ cursor: 'pointer', color: '#595959' }}
                onClick={() => handleLike(c.id)}
              />
            )}
          </Tooltip> */}

          {/* <Popover
            content={
              <EmojiPicker onEmojiClick={(e) => handleEmojiSelect(e, c.id)} />
            }
            trigger="click"
            open={showEmojiFor === c.id}
            onOpenChange={(open) => setShowEmojiFor(open ? c.id : null)}
          >
            <Tooltip title="React">
              <SmileOutlined style={{ cursor: 'pointer', color: '#595959' }} />
            </Tooltip>
          </Popover>

          {c.emoji && (
            <span style={{ marginLeft: 4, fontSize: 18 }}>{c.emoji}</span>
          )} */}

          <Tooltip title="Edit">
            <FormOutlined
              style={{ cursor: 'pointer', color: '#595959' }}
              onClick={() => {
                setEditingId(c.id);
                setEditText(c.comment_text);
              }}
            />
          </Tooltip>

          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ cursor: 'pointer', color: '#ff4d4f' }}
              onClick={() => handleDelete(c.id)}
            />
          </Tooltip>
        </div>

        {/* Reply Box  */}
        {replyingTo === c.id && (
          <div style={{ marginTop: 10, paddingLeft: 10 }}>
            <ReactQuill
              ref={editorRef}
              theme="snow"
              value={replyText}
              onChange={(value, delta, source, editor) => {
                setReplyText(value);

                const pos = editor.getSelection()?.index;
                setCursorPos(pos);

                const textUpToCursor = editor.getText(0, pos);
                const match = textUpToCursor.match(/@([a-zA-Z0-9_]*)$/);

                if (match) {
                  setTagSearch(match[1]);
                  setShowTagList(true);
                } else {
                  setShowTagList(false);
                }
              }}
            />
            {renderTagList()}

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <Space>
                <Button
                  type="primary"
                  onClick={() => handleReplyPost(rootParentId)}
                >
                  Save
                </Button>
                <Button onClick={() => setReplyingTo(null)}>Cancel</Button>
              </Space>
            </div>
          </div>
        )}

        {/* Recursive replies */}
        {Array.isArray(c.replies) &&
          c.replies.map((r) => renderCommentBox(r, level + 1, rootParentId))}
      </div>
    </div>
  );

  const renderTagList = () => {
    if (!showTagList) return null;

    return (
      <div
        style={{
          position: 'absolute',
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          marginTop: 4,
          maxHeight: 180,
          overflowY: 'auto',
          zIndex: 2000,
          width: 200,
        }}
      >
        {assigneeOptions
          ?.filter((u) =>
            u.username?.toLowerCase().includes(tagSearch.toLowerCase()),
          )
          .map((user) => (
            <div
              key={user.user_id}
              style={{
                padding: '8px 10px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
              }}
              onClick={() => handleTagInsert(user)}
            >
              @{user.username}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div className="jira-activity-tabs">
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k)}
          items={[
            { key: 'all', label: 'All' },
            { key: 'comments', label: 'Comments' },
            { key: 'history', label: 'History' },
            { key: 'worklog', label: 'Work log' },
          ]}
        />
      </div>

      {activeTab === 'comments' && (
        <>
          {/* Main Comment Box */}
          {isEditingMain ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  position: 'relative',
                }}
              >
                <ReactQuill
                  ref={editorRef}
                  theme="snow"
                  value={comment}
                  onChange={(value, delta, source, editor) => {
                    setComment(value);

                    const pos = editor.getSelection()?.index;
                    setCursorPos(pos);

                    const textUpToCursor = editor.getText(0, pos);
                    const match = textUpToCursor.match(/@([a-zA-Z0-9_]*)$/);

                    if (match) {
                      setTagSearch(match[1]);
                      setShowTagList(true);
                    } else {
                      setShowTagList(false);
                    }
                  }}
                  placeholder="Write a comment..."
                />
                {renderTagList()}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                  <Button type="primary" onClick={handlePost}>
                    Save
                  </Button>
                  <Button onClick={() => setIsEditingMain(false)}>
                    Cancel
                  </Button>
                </Space>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  position: 'relative',
                  padding: '12px',
                  borderRadius: 6,
                  minHeight: 90,
                  cursor: 'pointer',
                  border: '1px solid #d3d3d3',
                  paddingBottom: 60,
                }}
                onClick={() => setIsEditingMain(true)}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      comment || '<p style="color:gray;">Add a comment...</p>',
                  }}
                />

                {!isEditingMain && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 6,
                      left: 6,
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    {suggestions.map((sug, idx) => (
                      <span
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          onApplySuggestion(sug);
                        }}
                        style={{
                          background: '#f1f1f1',
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 12,
                          cursor: 'pointer',
                          border: '1px solid #ddd',
                        }}
                      >
                        {sug}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Divider />

          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Text>No comments yet</Text>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {comments.map((c) => renderCommentBox(c))}
            </div>
          )}
        </>
      )}
      {activeTab === 'history' && (
        <div style={{ padding: 20, color: '#888' }}>
          <Empty />
        </div>
      )}

      {activeTab === 'worklog' && (
        <div style={{ padding: 20, color: '#888' }}>
         <HistoryWorklog taskdata={taskdata} work_type={"ISSUE"} refreshKey={refreshKey} />
        </div>
      )}

      {activeTab === 'all' && (
        <div style={{ padding: 20 }}>
          <Empty />
        </div>
      )}
    </div>
  );
};

export default TaskCommentSection;
